class BigDropPurchaseOptions {
  constructor(element) {
    this.element = element;
    this.observer = new MutationObserver(() => this.refresh());
    element.addEventListener('change', () => this.refresh());
    this.refresh();
  }

  refresh() {
    // Our own enhancements must not retrigger the app-render observer.
    this.observer.disconnect();
    try {
      this.enhanceRows();
      this.enhanceDeliveryWindows();
    } finally {
      this.observer.observe(this.element, { childList: true, subtree: true });
    }
    this.selectDefaultSubscription();
  }

  // Preselect the subscription option the first time each app-rendered group
  // appears. The flag is set before clicking so the resulting change event
  // cannot loop, and so a later manual choice of one-time purchase sticks.
  selectDefaultSubscription() {
    this.element.querySelectorAll('.group_list').forEach((group) => {
      if (group.dataset.bigdropDefaulted === 'true') return;
      const plans = Array.from(group.querySelectorAll('input[data-radio-type="selling_plan"]'));
      if (!plans.length) return;

      group.dataset.bigdropDefaulted = 'true';
      const plan = plans.find((input) => input.checked) || plans.find((input) => !input.disabled);
      if (plan && !plan.checked) plan.click();
    });
  }

  enhanceDeliveryWindows() {
    this.element.querySelectorAll('.group_list').forEach((group) => {
      const inputs = Array.from(group.querySelectorAll('input[data-radio-type="selling_plan"]'));
      let control = group.querySelector('.bigdrop-delivery-window');
      const rows = inputs.map((input) => input.closest('.group_list_item'));
      if (inputs.length < 2 || rows.some((row) => !row)) {
        control?.remove();
        group.querySelectorAll('.bigdrop-plan-hidden, .bigdrop-plan-with-dropdown').forEach((row) => {
          row.classList.remove('bigdrop-plan-hidden', 'bigdrop-plan-with-dropdown');
        });
        return;
      }

      if (!control) {
        control = document.createElement('li');
        control.className = 'bigdrop-delivery-window';
        const label = document.createElement('label');
        label.textContent = 'Delivery frequency';
        const select = document.createElement('select');
        label.appendChild(select);
        control.appendChild(label);
        // Keep the select outside the app's radio labels. Native radios remain
        // responsible for updating selling_plan, prices and express checkout.
        group.appendChild(control);
        select.addEventListener('change', () => {
          const plans = Array.from(group.querySelectorAll('input[data-radio-type="selling_plan"]'));
          const plan = plans.find((input) => input.dataset.sellingPlanId === select.value);
          if (plan && !plan.disabled) plan.click();
        });
      }

      const select = control.querySelector('select');
      const selected = inputs.find((input) => input.checked)
        || inputs.find((input) => input.dataset.sellingPlanId === select.value && !input.disabled)
        || inputs.find((input) => !input.disabled) || inputs[0];
      const options = inputs.map((input) => {
        const option = document.createElement('option');
        option.value = input.dataset.sellingPlanId;
        option.textContent = input.closest('.group_list_item').querySelector(':scope > div > span')?.textContent.trim()
          || input.getAttribute('aria-label') || input.value;
        option.disabled = input.disabled;
        return option;
      });
      select.replaceChildren(...options);
      select.value = selected.dataset.sellingPlanId;
      select.disabled = inputs.every((input) => input.disabled);
      control.classList.toggle('is-selected', selected.checked);

      rows.forEach((row, index) => {
        const host = row.closest('label') || row;
        host.classList.toggle('bigdrop-plan-hidden', inputs[index] !== selected);
        row.classList.toggle('bigdrop-plan-with-dropdown', inputs[index] === selected);
      });
    });
  }

  enhanceRows() {
    this.element.querySelectorAll('.group_list_item').forEach((row) => {
      if (row.dataset.bigdropEnhanced === 'true') return;

      const input = row.querySelector('input[type="radio"]');
      const content = Array.from(row.children).find((child) => child.tagName === 'DIV');
      if (!input || !content) return;

      row.dataset.bigdropEnhanced = 'true';

      const isSubscription = input.dataset.radioType === 'selling_plan';
      const heading = document.createElement('div');
      heading.className = 'bigdrop-purchase-options__heading';

      const title = document.createElement('span');
      title.className = 'bigdrop-purchase-options__title';
      title.textContent = isSubscription ? 'Subscribe & Save' : 'One-Time Purchase';
      heading.appendChild(title);

      if (isSubscription) {
        const saving = input.getAttribute('aria-label')?.match(/\d+%\s*off/i)?.[0];
        if (saving) {
          const badge = document.createElement('span');
          badge.className = 'bigdrop-purchase-options__save';
          badge.textContent = saving;
          heading.appendChild(badge);
        }
      } else {
        Array.from(content.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            node.textContent = '';
          }
        });
      }

      content.prepend(heading);

      const price = content.querySelector('.in_widget_price');
      const compareAtPrice = input.dataset.variantCompareAtPrice;
      const selectedPrice = input.dataset.variantPrice;

      if (isSubscription && price && compareAtPrice && compareAtPrice !== selectedPrice) {
        const compare = document.createElement('s');
        compare.className = 'bigdrop-purchase-options__compare-price';
        compare.textContent = compareAtPrice;
        price.prepend(compare);
      }
    });
  }
}

document.querySelectorAll('.bigdrop-purchase-options').forEach((element) => {
  new BigDropPurchaseOptions(element);
});
